import { BACK_TO_HOME, normalizeId, sameId } from '@vnetwork/margarita-shell/iam';
import { K8S_V2_BASE, clusterPath, clusterTabPath } from '../paths';

/** The search parameter that names a foreign workspace: `/services/kubernetes/v2/clusters?organization_id=<customer id>`. */
const ORG_PARAM = 'organization_id';

/** Why Create cluster is off in another organization's workspace (the backend refuses the POST: kubernetes#8), and what to do. */
export const CREATE_IN_FOREIGN_REASON = 'Switch back to your organization to create a cluster.';

/** The title the Compute console gives a workspace its service does not answer for: the wording is shared on purpose. */
export const WORKSPACE_UNAVAILABLE_TITLE = "This workspace isn't available";
const WORKSPACE_UNAVAILABLE_TEXT = "You don't have access to it, or it was removed.";

/**
 * How a failed cluster list is shown. The backend answers 404 `{"message":"not found"}` for `?organization_id=` of an
 * organization the caller has no role in: in a foreign workspace that is "this workspace isn't available" (the Compute
 * wording), not the raw HTTP message. Any other failure, and a 404 on the home list, keeps its own message.
 */
export function clusterListFailure(input: {
  foreign: boolean;
  error: unknown;
}): { kind: 'unavailable'; title: string; text: string } | { kind: 'error'; description: string } {
  const status = (input.error as { response?: { status?: number } } | null | undefined)?.response?.status;
  if (input.foreign && status === 404) return { kind: 'unavailable', title: WORKSPACE_UNAVAILABLE_TITLE, text: WORKSPACE_UNAVAILABLE_TEXT };
  const message = (input.error as { message?: string } | null | undefined)?.message;
  return { kind: 'error', description: message || 'The cluster list is temporarily unavailable.' };
}

/** The organization a search string names; '' when it names none. */
export function organizationParam(search: string): string {
  return normalizeId(new URLSearchParams(search).get(ORG_PARAM));
}

/** The search that opens `organizationId`'s workspace; '' for the home workspace (`home`). */
export function workspaceSearch(organizationId: string, home: boolean): string {
  const id = normalizeId(organizationId);
  return home || !id ? '' : `?${ORG_PARAM}=${encodeURIComponent(id)}`;
}

export interface KubernetesWorkspaceOrg {
  /** The organization whose clusters the page lists; '' = the home workspace (the backend's default). */
  organizationId: string;
  /** Another organization's workspace is open. */
  foreign: boolean;
}

/**
 * Which workspace a Kubernetes page is in, from its search and the home organization. No parameter, or the home
 * organization itself, is the home workspace. While the home organization is not known yet a named organization counts
 * as foreign, so nothing that is foreign-only-unsafe (Create) is offered in the meantime.
 */
export function kubernetesWorkspaceOrg(search: string, homeOrganizationId: string | null | undefined): KubernetesWorkspaceOrg {
  const named = organizationParam(search);
  if (!named || sameId(named, homeOrganizationId)) return { organizationId: '', foreign: false };
  return { organizationId: named, foreign: true };
}

/** The links of the cluster pages; `search` (`workspaceSearch` of the open workspace, '' at home) keeps the workspace across them. */
export function clusterLinks(search: string) {
  return {
    list: `${K8S_V2_BASE}/clusters${search}`,
    vpcs: `${K8S_V2_BASE}/vpcs${search}`,
    cluster: (clusterId: string) => `${clusterPath(clusterId)}${search}`,
    tab: (clusterId: string, slug: string) => `${clusterTabPath(clusterId, slug)}${search}`,
  };
}

/**
 * Whether a cluster can be created from the workspace that is open, and if not, the reason shown and the action beside
 * it (`backLabel`: leave this workspace for the home one, where Create works).
 */
export function createClusterRule(foreign: boolean): { enabled: boolean; reason: string | null; backLabel: string | null } {
  return foreign
    ? { enabled: false, reason: CREATE_IN_FOREIGN_REASON, backLabel: BACK_TO_HOME }
    : { enabled: true, reason: null, backLabel: null };
}
